import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { PageHeaderPrimitives } from '@uhg-abyss/web/ui/PageHeader';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { IconMaterial } from '@uhg-abyss/web/ui/IconMaterial';
import { useOverlay } from '@uhg-abyss/web/hooks/useOverlay';
import { NavMenuPrimitives } from '@uhg-abyss/web/ui/NavMenu';
import { StateRouter } from '@uhg-abyss/web/ui/StateRouter';
import { Drawer } from '@uhg-abyss/web/ui/Drawer';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { usePageCollapsingHeader } from 'src/state/pageCollapsingHeader';
import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { useWindowSize } from '@sde/widgets-core/src/tools/useWindowSize';
import { usePPDLagoon } from '@sde/widgets-core/src/tools/lagoon';
import { TopNav } from './TopNav';
import { HeaderModals } from '../Header/HeaderModals';
import { NavMenu } from './NavMenu';
import { useScrollPosition } from './useScrollPosition';
import { HomeContent } from './HeaderSections/HomeContent';
import { TrainingAndSupportContent } from './HeaderSections/TrainingAndSupportContent';
import { PracticeManagementContent } from './HeaderSections/PracticeManagementContent';
import { ContentContainer } from './HeaderSections/ContentContainer';
import { ProfileContent } from './HeaderSections/ProfileContent';
import { PayerProvider } from './HeaderSections/PayerProvider';
import { AppSearchContainer } from './HeaderSections/AppSearchContainer';

const NavMenuWrapper = styled('div', {
  variants: {
    screenWidth: {
      fullScreenWidth: {
        zIndex: -1,
        position: 'absolute',
        top: '156px',
        width: '100%',
        height: '52px',
      },
      smallScreenWidth: {
        zIndex: -1,
        position: 'absolute',
        top: '39px',
        left: '29px',
        width: '30px',
        height: '30px',
      },
    },
  },
});

const StyledNavMenuPrimitivesRoot = styled(NavMenuPrimitives.Root, {
  position: 'relative',
  zIndex: 1074,
  // eslint-disable-next-line consistent-return
  dynamic: ({ isCollapsingHeaderStyle }) => {
    if (isCollapsingHeaderStyle) {
      return {
        display: 'none !important',
      };
    }
  },
});

const StyledLayoutStack = styled(Layout.Stack, {
  '&.abyss-layout-stack': {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    alignItems: 'flex-end',
    width: '100% !important',
    marginTop: '0px !important',
    marginBottom: '0px !important',
  },
  '&.abyss-layout-stack > div:nth-child(1)': {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    marginTop: '0px !important',
    marginBottom: '0px !important',
  },
  '&.abyss-layout-stack > div:nth-child(2)': {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    marginTop: '0px !important',
    marginBottom: '0px !important',
  },
  '&.abyss-layout-stack > div:nth-child(3)': {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    minHeight: '62px',
    marginTop: '0px !important',
    marginBottom: '0px !important',
  },
});

const CustomLayoutGroup = styled('div', {
  display: 'flex',
  marginLeft: '0px !important',
  width: '100% !important',
});

const FlexDiv = styled('div', {
  flex: '0.2 1 auto',
  paddingLeft: '10px',
});
const FlexDivLessShrink = styled('div', {
  flex: '1 0.8 auto',
  paddingLeft: '10px',
});

const HorizontalRule = styled('div', {
  height: 2,
  width: '100%',
  background: 'linear-gradient(90deg, transparent, $gray3 50%, $gray3)',
});

export const CollapsingHeader = ({ setDigitalSecurityError }) => {
  const { getHeaderContents } = usePPDLagoon();
  const isSmallScreen = useMediaQuery('(max-width: 983px)');
  const flyout = useOverlay('pageheader-model');
  const headerContents = getHeaderContents();
  const { user } = useUser.state();
  const { width } = useWindowSize();
  const { setIsSubMenuDropDownContent } = usePageCollapsingHeader.actions();
  const { showPageCollapsingHeader } = usePageCollapsingHeader.state();
  const { setShowPageCollapsingHeader } = usePageCollapsingHeader.actions();
  const scrollPos = useScrollPosition();

  useEffect(() => {
    if (scrollPos > 45) {
      setShowPageCollapsingHeader(true);
    } else {
      setShowPageCollapsingHeader(false);
    }
  }, [scrollPos, showPageCollapsingHeader]);

  useEffect(() => {
    if (isSmallScreen || showPageCollapsingHeader) {
      setIsSubMenuDropDownContent(true);
    } else {
      setIsSubMenuDropDownContent(false);
    }
    if (!isSmallScreen) {
      flyout.close();
    }
  }, [isSmallScreen, showPageCollapsingHeader]);

  const handleClick = (href) => {
    flyout.close();
    window.location.replace(href);
  };

  const handleAppSearch = () => {
    flyout.close();
  };

  const homeContentWidgets = {
    user,
    handleAppSearch,
    handleClick,
  };

  const appSearchContainerWidgets = {
    handleAppSearch,
  };

  return (
    <PageHeaderPrimitives.Provider>
      <PageHeaderPrimitives.Container
        css={{
          'abyss-page-header-root': {
            zIndex: 1075,
          },
          minHeight: '108px',
        }}
      >
        <If condition={isSmallScreen || showPageCollapsingHeader}>
          <PageHeaderPrimitives.HamburgerMenu
            onClick={() => flyout.open()}
            type="button"
            aria-label="Menu"
            aria-haspopup="dialog"
          >
            <IconMaterial icon="menu" color="$primary1" />{' '}
          </PageHeaderPrimitives.HamburgerMenu>
        </If>
        <PageHeaderPrimitives.Brandmark
          logoRedirect="#"
          css={{
            'abyss-page-header-logo': {
              width: 'max-content',
              flexShrink: '0.5',
              flexGrow: 0,
            },
          }}
        />
        <StyledLayoutStack alignLayout="right">
          <TopNav />
          <If condition={!showPageCollapsingHeader && !isSmallScreen}>
            <HorizontalRule />
          </If>
          <CustomLayoutGroup>
            <FlexDiv>
              <AppSearchContainer {...appSearchContainerWidgets} />
            </FlexDiv>
            <FlexDivLessShrink>
              <PayerProvider
                setDigitalSecurityError={setDigitalSecurityError}
              />
            </FlexDivLessShrink>
          </CustomLayoutGroup>
        </StyledLayoutStack>
        <StateRouter.Provider initialPath="/home">
          <Drawer position="left" title="Menu" model="pageheader-model">
            <NavMenuPrimitives.RouterHeader />
            <StateRouter.Routes>
              <StateRouter.Route path="/home" title="Back">
                <HomeContent {...homeContentWidgets} />
              </StateRouter.Route>
              <StateRouter.Route
                path="/home/trainingandsupport"
                title="Training & Support"
              >
                <TrainingAndSupportContent />
              </StateRouter.Route>
              <StateRouter.Route
                path="/home/practisemanagement"
                title="Practice Management"
              >
                <PracticeManagementContent />
              </StateRouter.Route>
              <StateRouter.Route path="/home/profile" title="Profile">
                <ProfileContent user={user} />
              </StateRouter.Route>
              <For each="headerContent" of={headerContents}>
                <If condition={headerContent.type === 'dropdown'}>
                  <StateRouter.Route
                    title={headerContent.name}
                    path={`/home/${headerContent.key}`}
                  >
                    <ContentContainer
                      heading={headerContent.name}
                      dropdownKey={headerContent.key}
                    />
                  </StateRouter.Route>
                </If>
              </For>
            </StateRouter.Routes>
          </Drawer>
        </StateRouter.Provider>
      </PageHeaderPrimitives.Container>
      <If condition={!isSmallScreen}>
        <StyledNavMenuPrimitivesRoot
          isCollapsingHeaderStyle={showPageCollapsingHeader}
        >
          <NavMenu />
          <NavMenuPrimitives.Viewport />
        </StyledNavMenuPrimitivesRoot>
      </If>
      <NavMenuWrapper
        screenWidth={width > 984 ? 'fullScreenWidth' : 'smallScreenWidth'}
        data-testid="nav-menu-wrapper"
      />
      <HeaderModals />
    </PageHeaderPrimitives.Provider>
  );
};

CollapsingHeader.propTypes = {
  setDigitalSecurityError: PropTypes.func.isRequired,
};
